import { useState, useEffect } from 'react';
import { Heart, Bell, X, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getFavorites, removeFavorite, setPriceAlert, FavoriteShoe } from '../utils/api';
import toast from 'react-hot-toast';
import { usePriceAlert } from '../hooks/usePriceAlert';

interface FavoritesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FavoritesModal: React.FC<FavoritesModalProps> = ({ isOpen, onClose }) => {
  const { isAuthenticated } = useAuth();
  const { priceAlertSettings, handleToggleGlobalAlert, handleThresholdChange } = usePriceAlert();
  const [favorites, setFavorites] = useState<FavoriteShoe[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPriceAlert, setShowPriceAlert] = useState<string | null>(null);
  const [alertPrice, setAlertPrice] = useState<string>('');

  // Load favorites when modal opens (2025-10-09 UPDATE: removed premium requirement)
  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;

    const loadFavorites = async () => {
      setLoading(true);
      try {
        const response = await getFavorites();
        if (response.success && response.data) {
          setFavorites(response.data.favorites);
        } else {
          throw new Error(response.error || 'Failed to load favorites');
        }
      } catch (error: any) {
        console.error('Error loading favorites:', error);
        toast.error(error.message || 'Failed to load favorites');
        setFavorites([]);
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, [isOpen, isAuthenticated]);


  const handleRemoveFavorite = async (shoeId: string) => {
    if (!confirm('Remove this shoe from your favorites?')) {
      return;
    }

    try {
      const response = await removeFavorite(shoeId);
      if (response.success) {
        setFavorites(prev => prev.filter(fav => fav.shoeId !== shoeId));
        toast.success('Shoe removed from favorites');
      } else {
        throw new Error(response.error || 'Failed to remove favorite');
      }
    } catch (error: any) {
      console.error('Error removing favorite:', error);
      toast.error(error.message || 'Failed to remove favorite');
    }
  };

  const handleSetPriceAlert = async (shoeId: string) => {
    const price = parseFloat(alertPrice);
    if (isNaN(price) || price <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    try {
      const response = await setPriceAlert(shoeId, price);
      if (response.success && response.data) {
        setFavorites(prev => prev.map(fav =>
          fav.shoeId === shoeId
            ? response.data!.favorite
            : fav
        ));
        setShowPriceAlert(null);
        setAlertPrice('');
        toast.success(`Price alert set! We'll email you when the price drops to $${price} or lower`);
      } else {
        throw new Error(response.error || 'Failed to set price alert');
      }
    } catch (error: any) {
      console.error('Error setting price alert:', error);
      toast.error(error.message || 'Failed to set price alert');
    }
  };

  const handleViewFullPage = () => {
    onClose();
    window.open('/favorites', '_blank');
  };


  const handleToggleShoeAlert = async (shoeId: string) => {
    const shoe = favorites.find(fav => fav.shoeId === shoeId);
    if (!shoe) return;

    const currentAlert = shoe.priceAlert;
    const willActivateAlert = !currentAlert || !currentAlert.isActive;

    // If activating alert and global is disabled, enable global first
    if (willActivateAlert && !priceAlertSettings.isEnabled) {
      handleToggleGlobalAlert();
      // Don't show duplicate toast, the global handler already shows one
    } else if (willActivateAlert) {
      toast.success(
        `Now watching ${shoe.shoeName}! We'll email you when the price drops ${priceAlertSettings.threshold}% or more.`,
        { duration: 4000 }
      );
    } else {
      toast('Email alert disabled for ' + shoe.shoeName, { duration: 3000 });
    }

    // Calculate target price for new alerts
    const targetPrice = shoe.currentPrice ? shoe.currentPrice * (1 - priceAlertSettings.threshold / 100) : 0;

    try {
      // Persist to backend
      if (willActivateAlert) {
        const response = await setPriceAlert(shoeId, targetPrice);
        if (response.success && response.data) {
          setFavorites(prev => prev.map(fav =>
            fav.shoeId === shoeId ? response.data!.favorite : fav
          ));
        } else {
          throw new Error(response.error || 'Failed to set price alert');
        }
      } else {
        // Disable alert by setting it to inactive (update local state only for now)
        // TODO: Add backend API to disable price alert
        setFavorites(prev => prev.map(fav => {
          if (fav.shoeId === shoeId && fav.priceAlert) {
            return {
              ...fav,
              priceAlert: {
                ...fav.priceAlert,
                isActive: false
              }
            };
          }
          return fav;
        }));
      }
    } catch (error: any) {
      console.error('Error toggling price alert:', error);
      toast.error(error.message || 'Failed to update price alert');
    }
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center flex-1 min-w-0">
            <Heart className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 mr-2 sm:mr-3 fill-current flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">My Favorite Shoes</h2>
              <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Quick access to your saved shoes</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* Desktop Price Alert Controls */}
            <div className="hidden sm:flex items-center space-x-2 bg-orange-50 px-3 py-2 rounded-lg border border-orange-200">
              <div className="flex items-center space-x-2">
                <Bell className="h-4 w-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-600">Price Alert:</span>
                
                <select
                  value={priceAlertSettings.threshold}
                  onChange={(e) => handleThresholdChange(Number(e.target.value))}
                  className="px-2 py-1 text-sm bg-white border border-orange-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-orange-600"
                >
                  <option value={5}>5%</option>
                  <option value={10}>10%</option>
                  <option value={15}>15%</option>
                  <option value={20}>20%</option>
                  <option value={30}>30%</option>
                  <option value={40}>40%</option>
                  <option value={50}>50%</option>
                </select>
                
                {/* Toggle Switch */}
                <button
                  onClick={handleToggleGlobalAlert}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                    priceAlertSettings.isEnabled ? 'bg-orange-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      priceAlertSettings.isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm font-medium text-orange-600">
                  {priceAlertSettings.isEnabled ? 'ON' : 'OFF'}
                </span>
              </div>
            </div>
            
            {/* Mobile Price Alert Toggle */}
            <button
              onClick={handleToggleGlobalAlert}
              className={`sm:hidden p-2 rounded-lg transition-colors border ${
                priceAlertSettings.isEnabled 
                  ? 'bg-orange-50 text-orange-600 border-orange-200' 
                  : 'bg-gray-100 text-gray-500 border-gray-200'
              }`}
              title={`Price Alerts ${priceAlertSettings.isEnabled ? 'ON' : 'OFF'} (${priceAlertSettings.threshold}%)`}
            >
              <Bell className="h-4 w-4" />
            </button>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {!isAuthenticated ? (
            <div className="text-center py-12">
              <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Sign in to view your favorites
              </h3>
              <p className="text-gray-600">
                Create an account to save your favorite shoes and get price alerts.
              </p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
              <span className="ml-3 text-gray-600">Loading your favorites...</span>
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                No favorites yet
              </h3>
              <p className="text-gray-600">
                Start adding shoes to your favorites from the recommendations!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {favorites.slice(0, 6).map((favorite) => (
                <div
                  key={favorite._id}
                  className="bg-gray-50 rounded-xl p-3 sm:p-4 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex space-x-3 sm:space-x-4">
                    <img
                      src={favorite.shoeImage}
                      alt={favorite.shoeName}
                      className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-gray-900 text-sm truncate flex-1 mr-2">
                          {favorite.shoeName}
                        </h4>
                        <button
                          onClick={() => handleToggleShoeAlert(favorite.shoeId)}
                          className={`p-1.5 rounded-lg transition-all duration-200 flex-shrink-0 ${
                            favorite.priceAlert?.isActive 
                              ? 'text-orange-600 bg-orange-50 hover:bg-orange-100 shadow-sm' 
                              : 'text-gray-500 hover:text-orange-600 hover:bg-orange-50'
                          }`}
                          title={
                            favorite.priceAlert?.isActive 
                              ? `✅ Alert active (${priceAlertSettings.threshold}% off)` 
                              : `Click to enable price alert (${priceAlertSettings.threshold}% off)`
                          }
                        >
                          <Bell className={`h-4 w-4 ${favorite.priceAlert?.isActive ? 'fill-current' : ''}`} />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">{favorite.shoeBrand}</p>
                      
                      <div className="mb-2">
                        {favorite.currentPrice && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="text-lg font-bold text-green-600">
                                ${favorite.currentPrice}
                              </div>
                              {favorite.originalPrice && favorite.originalPrice > favorite.currentPrice && (
                                <div className="text-sm text-gray-500 line-through">
                                  ${favorite.originalPrice}
                                </div>
                              )}
                            </div>
                            {favorite.discountPercentage && (
                              <div className="text-xs text-red-600 font-semibold">
                                {favorite.discountPercentage}% OFF
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex space-x-2">
                        <button
                          onClick={() => window.open(favorite.shoeUrl, '_blank')}
                          className="flex items-center justify-center px-2 py-1 bg-orange-600 text-white rounded text-xs font-medium hover:bg-orange-700 transition-colors flex-1"
                        >
                          Buy Now
                        </button>
                        <button
                          onClick={() => handleRemoveFavorite(favorite.shoeId)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Remove from favorites"
                        >
                          <Heart className="h-4 w-4 fill-current" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {favorites.length > 6 && (
                <div className="md:col-span-2 text-center py-4">
                  <p className="text-gray-600 mb-3">
                    Showing 6 of {favorites.length} favorites
                  </p>
                  <button
                    onClick={handleViewFullPage}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                  >
                    View All Favorites
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Price Alert Modal */}
        {showPriceAlert && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl p-6 max-w-sm w-full">
              <div className="flex items-center mb-4">
                <Bell className="h-5 w-5 text-orange-600 mr-2" />
                <h3 className="text-lg font-bold text-gray-900">Set Price Alert</h3>
              </div>
              
              <p className="text-gray-600 mb-4 text-sm">
                We'll notify you when the price drops to or below your target price.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Price ($)
                </label>
                <input
                  type="number"
                  value={alertPrice}
                  onChange={(e) => setAlertPrice(e.target.value)}
                  placeholder="Enter target price"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowPriceAlert(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSetPriceAlert(showPriceAlert)}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors text-sm"
                >
                  Set Alert
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesModal;